<template>
  <f7-popup id="popup-lesson-history-view">
    <f7-view>
      <f7-page class="chat">
        <f7-navbar no-hairline>
          <f7-nav-left>
            <f7-link @click="$f7.popup.close()">
              {{ $t(`close`) }}
            </f7-link>
          </f7-nav-left>
          <f7-nav-title>
            {{ `${lesson.teacher.name} ${lesson.teacher.lastName}` }}
          </f7-nav-title>
        </f7-navbar>

        <f7-block v-if="loading" class="loading-data">
          <f7-preloader></f7-preloader>
        </f7-block>

        <f7-block
          v-if="!loading && !lesson.messages.length"
          class="loading-data"
        >
          <span class="loading-data-error-message">{{ $t(`not_found`) }}</span>
        </f7-block>

        <f7-messages v-else ref="messages">
          <f7-message
            v-for="(message, index) in lesson.messages"
            :key="index"
            :type="message.type"
            :first="isFirstMessage(message, index)"
            :last="isLastMessage(message, index)"
            :tail="isTailMessage(message, index)"
            :avatar="
              message.type === 'received'
                ? `${BASE_PATH}${lesson.teacher.avatar}`
                : ''
            "
            :class="{ 'with-mark': message.mark }"
          >
            <!-- eslint-disable-next-line vue/no-v-html -->
            <span v-if="message.text" slot="text" v-html="message.text"></span>

            <!-- QUOTE TEMPLATE -->
            <span
              v-if="message.quote"
              slot="bubble-start"
              class="quote-messages"
            >
              <img src="static/quote.svg" alt="quote" />
              <div class="quote-messages-text">{{ message.quote }}</div>
              <div class="quote-messages-info">
                {{ format(new Date(message.quoteDate), 'd.L.yyyy, HH:mm:ss') }}
              </div>
            </span>

            <span
              v-if="message.mark"
              slot="bubble-end"
              class="message-mark-icon"
            >
              {{ message.mark === 'like' ? '👍' : '👎' }}
            </span>

            <span
              v-if="message.attachment && message.attachment.type === 'image'"
              slot="image"
            >
              <img
                :src="`${BASE_PATH}${message.attachment.src}`"
                :alt="message.attachment.name"
                @click="
                  openPhotoBrowser(
                    message.attachment.src,
                    message.attachment.type
                  )
                "
              />
            </span>

            <span
              v-if="
                message.attachment && message.attachment.type === 'image_url'
              "
              slot="image"
            >
              <img
                :src="message.attachment.src"
                :alt="message.attachment.name"
                @click="
                  openPhotoBrowser(
                    message.attachment.src,
                    message.attachment.type
                  )
                "
              />
            </span>

            <span
              v-if="message.attachment && message.attachment.type === 'audio'"
              slot="default"
            >
              <audio-player
                :src="`${BASE_PATH}${message.attachment.src}`"
                :type="message.type"
              ></audio-player>
            </span>

            <span
              v-if="
                message.attachment &&
                  message.attachment.type !== 'image' &&
                  message.attachment.type !== 'audio' &&
                  message.attachment.type !== 'image_url'
              "
              slot="default"
            >
              <file-card
                :name="message.attachment.name"
                :src="`${BASE_PATH}${message.attachment.src}`"
                :size="fileSize(message.attachment.size)"
                :type="message.attachment.type"
              ></file-card>
            </span>

            <span slot="end" class="message-time">
              {{ format(new Date(message.date), 'HH:mm:ss') }}
            </span>
          </f7-message>
        </f7-messages>
      </f7-page>
    </f7-view>
  </f7-popup>
</template>

<script>
import { format } from 'date-fns';
import { BASE_PATH } from '@/js/const/index.js';
import fileSize from '@/js/helpers/fileSize.js';

import FileCard from '@/components/FileCard.vue';
import AudioPlayer from '@/components/AudioPlayer.vue';

export default {
  name: 'LessonHistoryView',

  components: {
    FileCard,
    AudioPlayer
  },

  data() {
    return {
      loading: true,
      photoBrowserId: null
    };
  },

  computed: {
    BASE_PATH: () => BASE_PATH,

    format: () => format,

    fileSize: () => fileSize,

    photos() {
      const photos = this.lesson.messages
        .filter(
          (message) =>
            (message.attachment && message.attachment.type === 'image') ||
            (message.attachment && message.attachment.type === 'image_url')
        )
        .map(
          (message) =>
            `${message.attachment.type === 'image' ? BASE_PATH : ''}${
              message.attachment.src
            }`
        );

      return photos;
    },

    lesson() {
      return this.$store.state.lesson.history.find(
        (lesson) => lesson.id === Number(this.$f7route.params.id)
      );
    }
  },

  async created() {
    await this.$store.dispatch('lesson/getHistoryMessagesById', {
      id: this.$f7route.params.id
    });

    this.loading = false;
  },

  methods: {
    isFirstMessage(message, index) {
      const previousMessage = this.lesson.messages[index - 1];
      if (message.isTitle) return false;
      if (
        !previousMessage ||
        previousMessage.type !== message.type ||
        previousMessage.name !== message.name
      )
        return true;
      return false;
    },

    isLastMessage(message, index) {
      const nextMessage = this.lesson.messages[index + 1];
      if (message.isTitle) return false;
      if (
        !nextMessage ||
        nextMessage.type !== message.type ||
        nextMessage.name !== message.name
      )
        return true;
      return false;
    },

    isTailMessage(message, index) {
      const nextMessage = this.lesson.messages[index + 1];
      if (message.isTitle) return false;
      if (
        !nextMessage ||
        nextMessage.type !== message.type ||
        nextMessage.name !== message.name
      )
        return true;
      return false;
    },

    createPhotoBrowser() {
      this.photoBrowserId = this.$f7.photoBrowser.create({
        theme: 'dark',
        photos: this.photos
      });
    },

    async openPhotoBrowser(src, type) {
      const index = this.photos.indexOf(
        `${type === 'image' ? BASE_PATH : ''}${src}`
      );

      if (this.photoBrowserId) {
        this.photoBrowserId.destroy();

        await this.$nextTick();
        this.createPhotoBrowser();
        await this.$nextTick();
        this.photoBrowserId.open(index);
      } else {
        this.createPhotoBrowser();
        await this.$nextTick();
        this.photoBrowserId.open(index);
      }
    }
  }
};
</script>

<style lang="less">
#popup-lesson-history-view {
  .message-received .message-bubble {
    background-color: #f5f6f8;
  }
}
</style>
